import { SvgIcon } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

// Custom Checkbox Icons
export function CheckboxIcon(props) {
  return (
    <SvgIcon {...props}>
      <path d="M17.9 2.318A5 5 0 0 1 22.895 7.1l.005.217v10a5 5 0 0 1-4.783 4.995l-.217.005h-10a5 5 0 0 1-4.995-4.783l-.005-.217v-10a5 5 0 0 1 4.783-4.996l.217-.004h10Zm-.5 1.5h-9a4 4 0 0 0-4 4v9a4 4 0 0 0 4 4h9a4 4 0 0 0 4-4v-9a4 4 0 0 0-4-4Z" />
    </SvgIcon>
  );
}

export function CheckboxCheckedIcon(props) {
  return (
    <SvgIcon {...props}>
      <path d="M17 2a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm-1.625 7.255-4.13 4.13-1.75-1.75a.881.881 0 0 0-1.24 0c-.34.34-.34.89 0 1.24l2.38 2.37c.17.17.39.25.61.25.23 0 .45-.08.62-.25l4.75-4.75c.34-.34.34-.89 0-1.24a.881.881 0 0 0-1.24 0Z" />
    </SvgIcon>
  );
}

export function CheckboxIndeterminateIcon(props) {
  return (
    <SvgIcon {...props}>
      <path d="M17,2 C19.7614,2 22,4.23858 22,7 L22,7 L22,17 C22,19.7614 19.7614,22 17,22 L17,22 L7,22 C4.23858,22 2,19.7614 2,17 L2,17 L2,7 C2,4.23858 4.23858,2 7,2 L7,2 Z M15,11 L9,11 C8.44772,11 8,11.4477 8,12 C8,12.5523 8.44772,13 9,13 L15,13 C15.5523,13 16,12.5523 16,12 C16,11.4477 15.5523,11 15,11 Z" />
    </SvgIcon>
  );
}

// Shared segmented "pill" toggle style (light-blue selected on a white bordered container).
// Used by the COA structure toggle and the forecast Edit Value 90-day/month toggle.
// Apply to a MUI <ToggleButtonGroup sx={segmentedPillToggleSx}> with plain <ToggleButton>s.
export const segmentedPillToggleSx = {
  border: '1px solid #919EAB33',
  borderRadius: '8px',
  backgroundColor: 'white',
  padding: '2px',
  '& .MuiTouchRipple-root': { width: '100%' },
  '& .MuiToggleButtonGroup-grouped.Mui-selected': { borderRadius: '6px' },
  '& .MuiToggleButtonGroup-grouped': {
    margin: 0,
    border: 0,
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '13px',
    lineHeight: '20px',
    textTransform: 'none',
    '&.Mui-selected': {
      backgroundColor: '#DFECFF',
      color: '#2A5BA2',
      fontWeight: 600,
      padding: '4px 10px',
      '&:hover': {
        backgroundColor: '#1E497B',
        color: 'white',
      },
    },
  },
};

// Minimals Design System Theme Configuration
// Based on https://minimals.cc/components
const minimalTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#212B36', // Minimals primary color
      light: '#454F5B',
      dark: '#161C24',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#2A5BA2', // Blue accent color (used in checkboxes, radios)
      light: '#4A7BC2',
      dark: '#1E4282',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#FF5630', // Error red
      light: '#FF8A6B',
      dark: '#B71D18',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#FFAB00', // Warning orange
      light: '#FFC547',
      dark: '#B76E00', // Updated to match usage
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#00B8D9', // Info cyan
      light: '#33C9DC',
      dark: '#006C9C',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#22C55E', // Success green
      light: '#4ADE80',
      dark: '#118D57', // Updated to match published/final status color
      contrastText: '#FFFFFF',
    },
    // Custom status colors for plan statuses
    status: {
      draft: {
        main: '#637381',
        light: alpha('#919EAB', 0.16),
        dark: '#454F5B',
      },
      published: {
        main: '#118D57',
        light: alpha('#22C55E', 0.16),
        dark: '#065E49',
      },
      final: {
        main: '#118D57',
        light: alpha('#22C55E', 0.16),
        dark: '#065E49',
      },
      budget: {
        main: '#E02DA4',
        light: alpha('#E02DA4', 0.16),
        dark: '#9D3DD9',
      },
      forecast: {
        main: '#E9750F',
        light: alpha('#E9750F', 0.16),
        dark: '#C55A0D',
      },
      'what if': {
        main: '#169FA9',
        light: alpha('#169FA9', 0.16),
        dark: '#0D7A82',
      },
      overdue: {
        main: '#B71D18',
        light: alpha('#FF5630', 0.16),
        dark: '#7A0916',
      },
      'reaching soon': {
        main: '#B76E00',
        light: alpha('#FFAB00', 0.16),
        dark: '#B7791F',
      },
      'enough time': {
        main: '#006C9C',
        light: alpha('#00B8D9', 0.16),
        dark: '#004D6E',
      },
    },
    background: {
      default: '#F4F6F8', // Light grey background
      paper: '#FFFFFF', // White background for paper components
    },
    text: {
      primary: '#212B36', // Dark text color
      secondary: '#637381', // Muted grey for secondary text
      disabled: alpha('#212B36', 0.38),
    },
    grey: {
      50: '#F9FAFB',
      100: '#F4F6F8',
      200: '#DFE3E8',
      300: '#C4CDD5',
      400: '#919EAB',
      500: '#637381',
      600: '#454F5B',
      700: '#212B36',
      800: '#161C24',
      900: '#0D1219',
    },
    divider: alpha('#919EAB', 0.24),
    action: {
      active: '#212B36',
      hover: alpha('#212B36', 0.08),
      selected: alpha('#212B36', 0.16),
      disabled: alpha('#212B36', 0.38),
      disabledBackground: alpha('#212B36', 0.12),
    },
  },
  typography: {
    fontFamily: '"Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightSemiBold: 600,
    fontWeightBold: 700,
    h1: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.2,
      color: '#212B36',
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 700,
      lineHeight: 1.3,
      color: '#212B36',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: 1.4,
      color: '#212B36',
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#212B36',
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: '#212B36',
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: '#212B36',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#212B36',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.57,
      color: '#212B36',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#212B36',
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.57,
      color: '#637381',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.66,
      color: '#637381',
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 600,
      textTransform: 'none',
      lineHeight: 1.75,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      lineHeight: 2.66,
      letterSpacing: '0.5px',
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.05)',
    '0px 1px 3px rgba(0, 0, 0, 0.05), 0px 1px 2px rgba(0, 0, 0, 0.1)',
    '0px 2px 6px rgba(0, 0, 0, 0.05), 0px 1px 2px rgba(0, 0, 0, 0.1)',
    '0px 3px 6px rgba(0, 0, 0, 0.05), 0px 2px 4px rgba(0, 0, 0, 0.1)',
    '0px 4px 8px rgba(0, 0, 0, 0.05), 0px 2px 4px rgba(0, 0, 0, 0.1)',
    '0px 5px 10px rgba(0, 0, 0, 0.05), 0px 3px 6px rgba(0, 0, 0, 0.1)',
    '0px 6px 12px rgba(0, 0, 0, 0.05), 0px 3px 6px rgba(0, 0, 0, 0.1)',
    '0px 7px 14px rgba(0, 0, 0, 0.05), 0px 4px 8px rgba(0, 0, 0, 0.1)',
    '0px 8px 16px rgba(0, 0, 0, 0.05), 0px 4px 8px rgba(0, 0, 0, 0.1)',
    '0px 9px 18px rgba(0, 0, 0, 0.05), 0px 5px 10px rgba(0, 0, 0, 0.1)',
    '0px 10px 20px rgba(0, 0, 0, 0.05), 0px 5px 10px rgba(0, 0, 0, 0.1)',
    '0px 11px 22px rgba(0, 0, 0, 0.05), 0px 6px 12px rgba(0, 0, 0, 0.1)',
    '0px 12px 24px rgba(0, 0, 0, 0.05), 0px 6px 12px rgba(0, 0, 0, 0.1)',
    '0px 13px 26px rgba(0, 0, 0, 0.05), 0px 7px 14px rgba(0, 0, 0, 0.1)',
    '0px 14px 28px rgba(0, 0, 0, 0.05), 0px 7px 14px rgba(0, 0, 0, 0.1)',
    '0px 15px 30px rgba(0, 0, 0, 0.05), 0px 8px 16px rgba(0, 0, 0, 0.1)',
    '0px 16px 32px rgba(0, 0, 0, 0.05), 0px 8px 16px rgba(0, 0, 0, 0.1)',
    '0px 17px 34px rgba(0, 0, 0, 0.05), 0px 9px 18px rgba(0, 0, 0, 0.1)',
    '0px 18px 36px rgba(0, 0, 0, 0.05), 0px 9px 18px rgba(0, 0, 0, 0.1)',
    '0px 19px 38px rgba(0, 0, 0, 0.05), 0px 10px 20px rgba(0, 0, 0, 0.1)',
    '0px 20px 40px rgba(0, 0, 0, 0.05), 0px 10px 20px rgba(0, 0, 0, 0.1)',
    '0px 21px 42px rgba(0, 0, 0, 0.05), 0px 11px 22px rgba(0, 0, 0, 0.1)',
    '0px 22px 44px rgba(0, 0, 0, 0.05), 0px 11px 22px rgba(0, 0, 0, 0.1)',
    '0px 23px 46px rgba(0, 0, 0, 0.05), 0px 12px 24px rgba(0, 0, 0, 0.1)',
    '0px 24px 48px rgba(0, 0, 0, 0.05), 0px 12px 24px rgba(0, 0, 0, 0.1)',
  ],
  components: {
    // Button Component
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '6px 16px',
          textTransform: 'none',
          boxShadow: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          boxShadow: 'none',
          backgroundColor: '#212B36',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#161C24',
            boxShadow: 'none',
          },
          '&:disabled': {
            backgroundColor: alpha('#212B36', 0.12),
            color: alpha('#212B36', 0.38),
          },
        },
        outlined: {
          borderColor: alpha('#212B36', 0.5),
          color: '#212B36',
          '&:hover': {
            borderColor: '#212B36',
            backgroundColor: alpha('#212B36', 0.08),
            boxShadow: 'none',
          },
          '&:disabled': {
            borderColor: alpha('#212B36', 0.12),
            color: alpha('#212B36', 0.38),
          },
        },
        text: {
          color: '#212B36',
          '&:hover': {
            backgroundColor: alpha('#212B36', 0.08),
          },
          '&:disabled': {
            color: alpha('#212B36', 0.38),
          },
        },
        sizeSmall: {
          padding: '4px 12px',
          fontSize: '0.8125rem',
        },
        sizeLarge: {
          padding: '8px 24px',
          fontSize: '0.9375rem',
        },
      },
    },
    // ButtonGroup Component
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          '& .MuiButton-root': {
            boxShadow: 'none',
          },
        },
        grouped: {
          '&:not(:last-of-type)': {
            borderRight: `1px solid ${alpha('#919EAB', 0.24)}`,
          },
        },
      },
    },
    // TextField Component
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#FFFFFF',
            '& fieldset': {
              borderColor: alpha('#919EAB', 0.32),
              borderWidth: 1,
            },
            '&:hover fieldset': {
              borderColor: alpha('#212B36', 0.5),
            },
            '&.Mui-focused fieldset': {
              borderColor: '#212B36',
              borderWidth: 1,
            },
            '&.Mui-error fieldset': {
              borderColor: '#FF5630',
            },
            '&.Mui-disabled': {
              backgroundColor: alpha('#919EAB', 0.08),
              '& fieldset': {
                borderColor: alpha('#919EAB', 0.24),
              },
            },
          },
          '& .MuiInputLabel-root': {
            color: '#637381',
            '&.Mui-focused': {
              color: '#212B36',
            },
            '&.Mui-error': {
              color: '#FF5630',
            },
          },
          '& .MuiOutlinedInput-input': {
            color: '#212B36',
            fontSize: '0.875rem',
            '&::placeholder': {
              color: '#919EAB',
              opacity: 1,
            },
          },
          '& .MuiFormHelperText-root': {
            marginTop: '4px',
            fontSize: '0.75rem',
            color: '#637381',
            '&.Mui-error': {
              color: '#FF5630',
            },
          },
        },
      },
    },
    // InputBase Component
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: '0.875rem',
          '&.Mui-disabled': {
            backgroundColor: alpha('#919EAB', 0.08),
            color: alpha('#212B36', 0.38),
          },
        },
        input: {
          '&::placeholder': {
            color: '#919EAB',
            opacity: 1,
          },
        },
      },
    },
    // Select Component
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-disabled': {
            backgroundColor: alpha('#919EAB', 0.08),
          },
        },
        select: {
          padding: '12px 14px',
          fontSize: '0.875rem',
          '&:focus': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    // ListSubheader Component
    MuiListSubheader: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          color: '#637381',
          padding: '8px 16px',
          lineHeight: 1.66,
          letterSpacing: '0.5px',
        },
      },
    },
    // IconButton Component
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: 8,
          color: '#637381',
          '&:hover': {
            backgroundColor: alpha('#212B36', 0.08),
            color: '#212B36',
          },
          '&.Mui-disabled': {
            color: alpha('#212B36', 0.38),
          },
        },
        colorPrimary: {
          color: '#212B36',
          '&:hover': {
            backgroundColor: alpha('#212B36', 0.08),
          },
        },
        sizeSmall: {
          padding: 4,
        },
      },
    },
    // Tooltip Component
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#212B36',
          color: '#FFFFFF',
          fontSize: '0.75rem',
          padding: '4px 8px',
          borderRadius: 4,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
        },
        arrow: {
          color: '#212B36',
        },
      },
    },
    // Chip Component
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          fontWeight: 500,
          height: 24,
          borderRadius: 6,
          backgroundColor: alpha('#212B36', 0.08),
          color: '#212B36',
          '&.MuiChip-colorPrimary': {
            backgroundColor: '#212B36',
            color: '#FFFFFF',
          },
          '&.MuiChip-colorSecondary': {
            backgroundColor: '#2A5BA2',
            color: '#FFFFFF',
          },
          '&.MuiChip-colorSuccess': {
            backgroundColor: alpha('#22C55E', 0.16),
            color: '#065E49',
          },
          '&.MuiChip-colorError': {
            backgroundColor: alpha('#FF5630', 0.16),
            color: '#7A0916',
          },
          '&.MuiChip-colorWarning': {
            backgroundColor: alpha('#FFAB00', 0.16),
            color: '#B7791F',
          },
        },
        label: {
          padding: '0 8px',
        },
        sizeSmall: {
          height: 20,
          fontSize: '0.6875rem',
        },
      },
    },
    // Checkbox Component
    MuiCheckbox: {
      defaultProps: {
        icon: <CheckboxIcon />,
        checkedIcon: <CheckboxCheckedIcon />,
        indeterminateIcon: <CheckboxIndeterminateIcon />,
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          padding: theme.spacing(1),
          color: alpha('#212B36', 0.5),
          '&:hover': {
            backgroundColor: alpha('#212B36', 0.08),
          },
          ...(ownerState.size === 'small' && {
            '& svg': { width: 20, height: 20 },
            "&.Mui-checked": { color: "#2A5BA2" },
            '&.MuiCheckbox-indeterminate': {
              color: "#2A5BA2",
            },
          }),
          ...(ownerState.size === 'medium' && {
            '& svg': { width: 24, height: 24 },
            "&.Mui-checked": { color: "#2A5BA2" },
            '&.MuiCheckbox-indeterminate': {
              color: "#2A5BA2",
            },
          }),
        }),
      },
    },
    // Radio Component
    MuiRadio: {
      styleOverrides: {
        root: {
          color: alpha('#212B36', 0.5),
          '&:hover': {
            backgroundColor: alpha('#212B36', 0.08),
          },
          '&.Mui-checked': {
            color: '#2A5BA2',
          },
          '&.Mui-disabled': {
            color: alpha('#212B36', 0.38),
          },
        },
      },
    },
    // Switch Component
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 44,
          height: 24,
          padding: 0,
        },
        switchBase: {
          padding: 2,
          '&.Mui-checked': {
            transform: 'translateX(20px)',
            color: '#FFFFFF',
            '& + .MuiSwitch-track': {
              backgroundColor: '#2A5BA2',
              opacity: 1,
            },
          },
          '&.Mui-disabled': {
            opacity: 0.38,
          },
        },
        thumb: {
          width: 20,
          height: 20,
          boxShadow: 'none',
        },
        track: {
          borderRadius: 12,
          backgroundColor: alpha('#919EAB', 0.5),
          opacity: 1,
        },
      },
    },
    // FormControlLabel Component
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginRight: 0,
        },
        label: {
          fontSize: '0.875rem',
          color: '#212B36',
          '&.Mui-disabled': {
            color: alpha('#212B36', 0.38),
          },
        },
      },
    },
    // Autocomplete Component
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& span.MuiAutocomplete-tag': {
            fontSize: '0.75rem',
            lineHeight: '24px',
            borderRadius: 6,
            backgroundColor: alpha('#212B36', 0.08),
            color: '#212B36',
            margin: '2px 4px 2px 0',
            padding: '0 8px',
          },
        },
        paper: {
          borderRadius: 8,
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.1)',
          border: `1px solid ${alpha('#919EAB', 0.24)}`,
        },
        listbox: {
          padding: '4px 0',
        },
        option: {
          fontSize: '0.875rem',
          padding: '8px 16px',
          margin: '2px 8px',
          borderRadius: 4,
          '&:hover': {
            backgroundColor: alpha('#212B36', 0.08),
          },
          '&[aria-selected="true"]': {
            backgroundColor: alpha('#212B36', 0.16),
            fontWeight: 600,
          },
        },
      },
    },
    // ToggleButtonGroup Component
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          display: 'flex',
          gap: 8,
          '& .MuiToggleButton-root': {
            border: `1px solid ${alpha('#919EAB', 0.32)}`,
            '&:not(:first-of-type)': {
              marginLeft: 0,
            },
          },
        },
      },
    },
    // ToggleButton Component
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '6px 16px',
          textTransform: 'none',
          fontSize: '0.875rem',
          fontWeight: 600,
          color: '#637381',
          borderColor: alpha('#919EAB', 0.32),
          '&:hover': {
            backgroundColor: alpha('#212B36', 0.08),
            borderColor: alpha('#212B36', 0.5),
          },
          '&.Mui-selected': {
            color: '#FFFFFF',
            backgroundColor: '#2A5BA2',
            borderColor: '#2A5BA2',
            '&:hover': {
              backgroundColor: '#1E4282',
            },
          },
          '&.Mui-disabled': {
            color: alpha('#212B36', 0.38),
            borderColor: alpha('#212B36', 0.12),
          },
        },
      },
    },
    // Dialog Component
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
        },
        paperFullScreen: {
          borderRadius: 0,
        },
      },
    },
    // DialogTitle Component
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '24px 24px 16px',
          fontSize: '1.25rem',
          fontWeight: 700,
          color: '#212B36',
        },
      },
    },
    // DialogContent Component
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          color: '#212B36',
        },
      },
    },
    // DialogActions Component
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          gap: 8,
          '& > :not(style) ~ :not(style)': {
            marginLeft: 0,
          },
          '& .MuiButton-root': {
            height: 36,
            minHeight: 36,
          },
        },
      },
    },
    // Alert Component
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '12px 16px',
          fontSize: '0.875rem',
        },
        standardSuccess: {
          backgroundColor: alpha('#22C55E', 0.16),
          color: '#065E49',
          '& .MuiAlert-icon': {
            color: '#22C55E',
          },
        },
        standardError: {
          backgroundColor: alpha('#FF5630', 0.16),
          color: '#7A0916',
          '& .MuiAlert-icon': {
            color: '#FF5630',
          },
        },
        standardWarning: {
          backgroundColor: alpha('#FFAB00', 0.16),
          color: '#B7791F',
          '& .MuiAlert-icon': {
            color: '#FFAB00',
          },
        },
        standardInfo: {
          backgroundColor: alpha('#00B8D9', 0.16),
          color: '#006C9C',
          '& .MuiAlert-icon': {
            color: '#00B8D9',
          },
        },
      },
    },
    // Snackbar Component
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiSnackbarContent-root': {
            borderRadius: 8,
            boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    // Breadcrumbs Component
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          '& .MuiBreadcrumbs-separator': {
            margin: '0 8px',
            color: '#919EAB',
          },
        },
        ol: {
          flexWrap: 'nowrap',
        },
      },
    },
    // Link Component
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#212B36',
          textDecoration: 'none',
          fontSize: '0.875rem',
          fontWeight: 400,
          '&:hover': {
            textDecoration: 'underline',
            color: '#161C24',
          },
          '&.Mui-disabled': {
            color: alpha('#212B36', 0.38),
            pointerEvents: 'none',
          },
        },
      },
    },
    // Typography Component
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.MuiTypography-gutterBottom': {
            marginBottom: '0.35em',
          },
        },
      },
    },
    // Skeleton Component
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: alpha('#919EAB', 0.16),
        },
      },
    },
    // Paper Component
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundImage: 'none',
          boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.1)',
        },
        elevation0: {
          boxShadow: 'none',
        },
        elevation1: {
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
        },
        elevation2: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05), 0px 1px 2px rgba(0, 0, 0, 0.1)',
        },
        elevation3: {
          boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.05), 0px 1px 2px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    // Card Component
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    // AppBar Component
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          backgroundColor: '#FFFFFF',
          color: '#212B36',
          borderBottom: `1px solid ${alpha('#919EAB', 0.24)}`,
        },
      },
    },
    // LinearProgress Component
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 8,
          borderRadius: 4,
          backgroundColor: alpha('#212B36', 0.08),
        },
        bar: {
          borderRadius: 4,
          backgroundColor: '#212B36',
        },
      },
    },
    // Tabs Component
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderBottom: 'none !important',
          '& .MuiTabs-flexContainer': {
            gap: 0,
            borderBottom: 'none !important',
          },
          '& .MuiTabs-scroller': {
            overflow: 'auto !important',
            borderBottom: 'none !important',
          },
        },
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
          backgroundColor: '#212B36',
        },
        scrollButtons: {
          '&.Mui-disabled': {
            opacity: 0.3,
          },
        },
      },
    },
    // Tab Component
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 40,
          padding: '10px 16px',
          fontSize: '0.875rem',
          fontWeight: 600,
          textTransform: 'none',
          color: '#637381',
          opacity: 1,
          transition: 'color 0.2s ease',
          '&:hover': {
            color: '#212B36',
          },
          '&.Mui-selected': {
            color: '#212B36',
          },
          '&.Mui-disabled': {
            color: alpha('#212B36', 0.38),
          },
        },
      },
    },
    // Slider Component
    MuiSlider: {
      styleOverrides: {
        root: {
          color: '#212B36',
          height: 4,
          '& .MuiSlider-thumb': {
            width: 16,
            height: 16,
            boxShadow: 'none',
            border: `2px solid #FFFFFF`,
            '&:hover': {
              boxShadow: `0px 0px 0px 8px ${alpha('#212B36', 0.16)}`,
            },
            '&.Mui-focusVisible': {
              boxShadow: `0px 0px 0px 8px ${alpha('#212B36', 0.16)}`,
            },
          },
          '& .MuiSlider-track': {
            border: 'none',
          },
          '& .MuiSlider-rail': {
            opacity: 0.3,
            backgroundColor: alpha('#919EAB', 0.5),
          },
        },
      },
    },
  },
});

export default minimalTheme;
